import React, { useState, useEffect, useRef } from 'react';
import { Plus, ChevronDown } from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { useSelector } from 'react-redux';
import { useThemeColor } from '../../../hooks/useThemeColor';

const TaskModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Aufgabe hinzufügen',
  description = 'Geben Sie einen Namen ein und wählen Sie eine Organisation (optional).',
  confirmText = 'Hinzufügen',
  confirmColor = 'bg-custom-yellow'
}) => {
  const [taskName, setTaskName] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const { addToast } = useToast();
  const { aispaceColor } = useThemeColor();
  const orgDropdownRef = useRef(null);

  // Get organizations from Redux store
  const { organizations } = useSelector(state => state.organizations);

  useEffect(() => {
    if (isOpen) {
      setTaskName('');
      setSelectedOrganization('');
      setOrgDropdownOpen(false);
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target)) setOrgDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const trimmedName = taskName.trim();

    if (!trimmedName) {
      addToast('Bitte geben Sie einen gültigen Namen ein', 'error');
      return;
    }

    const organizationId = selectedOrganization || null;
    onConfirm(trimmedName, organizationId);
    setTaskName('');
    setSelectedOrganization('');
  };

  const selectedOrgName = selectedOrganization
    ? organizations.find(o => o.organization_id === selectedOrganization)?.organization_name
    : null;

  return (
    <div className="fixed inset-0 bg-[#D6D6D6B2] bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 flex flex-col items-center">
          <div className="rounded-full bg-blue-100 p-3 mb-4">
            <Plus className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">{title}</h2>
          <p className="text-gray-600 text-center mb-4">{description}</p>

          <div className="w-full space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aufgabenname *
              </label>
              <input
                type="text"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Aufgabenname eingeben"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisation (optional)
              </label>
              <div className="relative" ref={orgDropdownRef}>
                <button
                  type="button"
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  className="w-full px-4 py-2.5 pr-10 text-[14px] text-left border rounded-lg bg-white focus:outline-none cursor-pointer"
                  style={{ borderColor: orgDropdownOpen ? (aispaceColor || '#3B82F6') : '#D1D5DB' }}
                >
                  <span className={selectedOrganization ? 'text-[#111827]' : 'text-[#9CA3AF]'}>
                    {selectedOrgName || 'Global (für alle Benutzer)'}
                  </span>
                </button>
                <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                {orgDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-[200px] overflow-y-auto">
                    <div
                      onClick={() => { setSelectedOrganization(''); setOrgDropdownOpen(false); }}
                      className="px-4 py-2.5 text-[14px] text-[#9CA3AF] hover:bg-[#F9FAFB] cursor-pointer"
                    >
                      Global (für alle Benutzer)
                    </div>
                    {organizations.map((org) => (
                      <div
                        key={org.organization_id}
                        onClick={() => { setSelectedOrganization(org.organization_id); setOrgDropdownOpen(false); }}
                        className={`px-4 py-2.5 text-[14px] cursor-pointer transition-colors ${
                          selectedOrganization === org.organization_id ? 'font-medium' : 'text-[#374151] hover:bg-[#F9FAFB]'
                        }`}
                        style={selectedOrganization === org.organization_id ? { backgroundColor: `${(aispaceColor || '#3B82F6')}10`, color: aispaceColor || '#3B82F6' } : {}}
                      >
                        {org.organization_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-4 w-full mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleConfirm}
              className={`flex-1 px-4 py-2 ${confirmColor} text-white rounded hover:opacity-90 cursor-pointer transition-colors`}
              disabled={!taskName.trim()}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
